import React, { useMemo } from "react";
import { format, parseISO } from "date-fns";
import { Calendar as CalendarIcon, Clock } from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { cn } from "@/lib/utils";

/**
 * DateTimePicker - Shadcn Calendar + 30-minute time-of-day list.
 *
 * @param {string} value  ISO local string "YYYY-MM-DDTHH:mm" (or empty)
 * @param {(v:string)=>void} onChange  receives ISO local string
 * @param {{start:string,end:string}[]} allowedSlots  optional weekday-specific slots like [{start:"09:00",end:"17:00"}]
 * @param {string} testId
 * @param {string} placeholder
 * @param {number} stepMin step in minutes for the time list (default 30)
 */
export default function DateTimePicker({
  value = "",
  onChange,
  allowedSlots,
  testId = "datetime-picker",
  placeholder = "Pick date & time",
  stepMin = 30,
}) {
  const dt = useMemo(() => {
    if (!value) return null;
    try {
      // value is "YYYY-MM-DDTHH:mm" — parse as local
      return parseISO(value.length === 16 ? `${value}:00` : value);
    } catch {
      return null;
    }
  }, [value]);

  const selectedDate = dt;
  const hhmm = dt ? format(dt, "HH:mm") : "";

  const setDate = (d) => {
    if (!d) return;
    const time = hhmm || "09:00";
    const iso = `${format(d, "yyyy-MM-dd")}T${time}`;
    onChange?.(iso);
  };

  const setTime = (newHhmm) => {
    const base = selectedDate || new Date();
    const iso = `${format(base, "yyyy-MM-dd")}T${newHhmm}`;
    onChange?.(iso);
  };

  const times = useMemo(() => {
    const list = [];
    for (let m = 0; m < 24 * 60; m += stepMin) {
      const h = String(Math.floor(m / 60)).padStart(2, "0");
      const mn = String(m % 60).padStart(2, "0");
      list.push(`${h}:${mn}`);
    }
    return list;
  }, [stepMin]);

  const isAllowed = (t) => {
    if (!allowedSlots || allowedSlots.length === 0) return true;
    return allowedSlots.some(s => (s.start || "00:00") <= t && t < (s.end || "23:59"));
  };

  const label = dt
    ? `${format(dt, "EEE, MMM d, yyyy")} · ${format(dt, "HH:mm")}`
    : placeholder;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal rounded-sm h-10",
            !dt && "text-muted-foreground"
          )}
          data-testid={`${testId}-trigger`}
        >
          <CalendarIcon className="mr-2 w-4 h-4" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 flex"
        align="start"
        data-testid={`${testId}-popover`}
      >
        <div className="border-r border-border">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setDate}
            disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
            initialFocus
          />
        </div>
        <div className="flex flex-col w-32 max-h-72 overflow-y-auto" data-testid={`${testId}-times`}>
          <div className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
            <Clock className="w-3.5 h-3.5" /> Time
          </div>
          {times.map(t => {
            const allowed = isAllowed(t);
            const active = t === hhmm;
            return (
              <button
                key={t}
                type="button"
                onClick={() => allowed && setTime(t)}
                disabled={!allowed}
                className={cn(
                  "px-3 py-2 text-left text-sm border-b border-border last:border-0 transition-colors",
                  active && "bg-primary text-primary-foreground",
                  !active && allowed && "hover:bg-accent",
                  !allowed && "opacity-30 cursor-not-allowed line-through"
                )}
                data-testid={`${testId}-time-${t}`}
              >
                {t}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
